import { Button } from "@/components/ui/baseUi/button"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { useTransactionPdfDownload } from "@/modules/finances/hooks/useTransactionPdfDownload"
import { PaymentsToValidateEnumApi } from "@/modules/finances/paymentsToValidate/gateway/paymentsToValidateApi"
import { usePaymentsToValidateActions } from "@/modules/finances/paymentsToValidate/hooks/usePaymentsToValidateActions"
import { cn } from "@/modules/lib/utils"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { word } from "@modules/core/utils/i18n"
import { OutOfBandTransactionStatus } from "@modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { InvoiceDetails } from "@modules/finances/transactions/components/invoiceDetails"
import { InvoiceDetailsTable } from "@modules/finances/transactions/components/invoiceDetailsTable"
import { InvoicePaymentDetails } from "@modules/finances/transactions/components/invoicePaymentDetails"
import {
  TransactionRefundModal,
  type TransactionRefundModalRef,
} from "@modules/finances/transactions/components/transactionRefundModal"
import { ApiFinanceType, TransactionStatus } from "@modules/finances/types/financesType"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { DownloadIcon, HandCoinsIcon, LoaderCircle, XIcon } from "lucide-react"
import { useRef } from "react"

type TransactionInvoiceLayoutProps = {
  apiFinanceType: ApiFinanceType
  transactionDetails: TransactionDetailsEntity
  className?: string
} & React.ComponentProps<"div">

export function TransactionInvoiceLayout({
  transactionDetails,
  className,
  apiFinanceType,
  ...props
}: TransactionInvoiceLayoutProps) {
  const { result: currentBox } = useCurrentBox()
  const confirmPopup = useConfirmPopup()
  const { hideSidebar } = useSidebar()

  const { abandonPaymentMutation } = usePaymentsToValidateActions()
  const { downloadTransactionPdf, isDownloading } = useTransactionPdfDownload()
  const canEditFinancial = useBoxStaffPermission(BoxStaffPermissions.EDIT_FINANCIAL) || !currentBox

  const transactionRefundModal = useRef<TransactionRefundModalRef>(null)

  const isRefundEnabled = transactionDetails.refundable

  const isRemoveFromOverdueListEnabled = transactionDetails.outOfBandStatus === OutOfBandTransactionStatus.PAST_DUE

  const canFetchInvoice =
    transactionDetails.status === TransactionStatus.DISPUTED ||
    transactionDetails.status === TransactionStatus.REFUNDED ||
    transactionDetails.status === TransactionStatus.SUCCEEDED ||
    (transactionDetails.outOfBandStatus !== null &&
      [
        OutOfBandTransactionStatus.PAID,
        OutOfBandTransactionStatus.CARD,
        OutOfBandTransactionStatus.CASH,
        OutOfBandTransactionStatus.CHECK,
      ].includes(transactionDetails.outOfBandStatus))

  const fetchInvoice = async () => {
    if (canFetchInvoice) {
      await downloadTransactionPdf({
        transactionId: transactionDetails.transactionId,
        invoiceNumber: transactionDetails.invoiceNumber,
      })
      return
    }
  }

  const handleConfirmRemoveFromOverdueList = () => {
    if (!!transactionDetails.outOfBandStatus && !abandonPaymentMutation.isPending) {
      confirmPopup.show({
        title: word("finances.transaction.removeFromOverdueList.confirm.title"),
        message: word("finances.transaction.removeFromOverdueList.confirm.content"),
        accept: async () => {
          await abandonPaymentMutation.mutateAsync(
            {
              api:
                apiFinanceType === ApiFinanceType.BOX
                  ? PaymentsToValidateEnumApi.OWNER
                  : PaymentsToValidateEnumApi.ATHLETE,
              transactionId: transactionDetails.transactionId,
            },
            {
              onSuccess: () => {
                toast.success(word("finances.transaction.removeFromOverdueList.success"), {
                  classNames: successToastClassName(),
                })

                hideSidebar()
              },
              onError: () => {
                toast.error(word("finances.transaction.removeFromOverdueList.error"), {
                  classNames: errorToastClassName(),
                })
              },
            },
          )
        },
      })
    }
  }

  return (
    <div className={cn("flex w-full flex-col gap-5 pt-2 pb-10", className)} {...props}>
      <InvoiceDetails transactionDetails={transactionDetails} />

      <InvoicePaymentDetails transactionDetails={transactionDetails} apiFinanceType={apiFinanceType} />

      <InvoiceDetailsTable transactionDetails={transactionDetails} />

      {(isRefundEnabled || canFetchInvoice) && (
        <div className="flex justify-between">
          {isRefundEnabled && (
            <Button
              variant="outline"
              size="lg"
              onClick={() => {
                transactionRefundModal.current?.open()
              }}
              disabled={!canEditFinancial}
            >
              {word("box.transaction.refund.modal.alert.action")}
              <HandCoinsIcon color="currentColor" size={22} className="text-textDefault-body" />
            </Button>
          )}

          {canFetchInvoice && (
            <div className="ml-auto flex">
              <Button
                variant="primary"
                onClick={() => {
                  void fetchInvoice()
                }}
                disabled={isDownloading}
                size="lg"
                isLoading={isDownloading}
              >
                {word("finances.array.invoice.download")}
                {isDownloading ? (
                  <LoaderCircle />
                ) : (
                  <DownloadIcon color="currentColor" size={22} className="text-textDefault-body" />
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {isRemoveFromOverdueListEnabled && (
        <Button
          variant="underline"
          className="w-fit"
          size="lg"
          onClick={() => {
            handleConfirmRemoveFromOverdueList()
          }}
          disabled={!canEditFinancial}
        >
          <XIcon color="currentColor" size={22} className="text-textDefault-body" />
          {word("finances.transaction.removeFromOverdueList.label")}
        </Button>
      )}

      {transactionDetails instanceof TransactionDetailsEntity && (
        <TransactionRefundModal transactionDetails={transactionDetails} ref={transactionRefundModal} />
      )}
    </div>
  )
}
